import { css } from '@emotion/react';
import styled from '@emotion/styled';

import IcChevronRightRegular from 'src/assets/images/icChevronRightRegular.svg';
import IcHashtagFill from 'src/assets/images/icHashtagFill.svg';
import IcLanguageFill from 'src/assets/images/icLanguageFill.svg';
import IcPersonFill from 'src/assets/images/icPersonFill.svg';
import IcWarningFill from 'src/assets/images/icWarningFill.svg';
import ModalLayoutBase, {
  ModalContent as ModalContentBase,
  ModalFooter as ModalFooterBase,
} from 'src/components/ModalLayout';
import { GridDropZone } from 'src/components/react-grid-drag/GridDropZone';

export const CARD_HEIGHT = 148;

export const ProfileImageBase = (theme: Theme) => css`
  height: ${CARD_HEIGHT}px;
  width: calc(100% - 9px);
  background-color: ${theme.color.surface4};
  border-radius: 8px;
  cursor: pointer;
  background-position: center;
  background-repeat: no-repeat;
  display: block;
`;

export const ProfileImageWrap = styled(GridDropZone)`
  ${({ theme }) => (theme.isRtl ? 'margin-right' : 'margin-left')}: -4px;
  touch-action: none;
  overscroll-behavior: contain;
  > * {
    display: flex;
    flex-direction: row;
    justify-content: center;
  }
`;

export const ModalContent = styled(ModalContentBase)`
  max-height: 580px;
  overflow-y: scroll;
  padding-top: 16px;
  ${({ theme }) => theme.screenSize.mobile} {
    max-height: 100%;
  }
`;

export const ModalLayout = styled(ModalLayoutBase)`
  width: 400px;
  ${({ theme }) => theme.screenSize.mobile} {
    width: 100%;
  }
`;

export const ModalFooter = styled(ModalFooterBase)`
  flex-direction: row;
`;

export const Subtitle = styled.div`
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.contentDeep};
  margin-bottom: 16px;
`;

export const Desc = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 10px;
  line-height: 12px;
  color: ${({ theme }) => theme.color.content10};
`;

export const Section = styled.div`
  margin-bottom: 24px;
`;

const FieldBase = (theme: Theme) => css`
  outline: none;
  width: 100%;
  box-sizing: border-box;
  border: none;
  border-radius: 8px;
  color: ${theme.color.contentDeep};
  background-color: ${theme.color.surface4};
`;

const InlineFieldBase = (theme: Theme) => css`
  ${FieldBase(theme)}
  font-size: 14px;
  line-height: 21px;
  & + & {
    margin-top: 16px;
  }
`;

export const Icon = styled.img`
  width: 16px;
  height: 16px;
`;

const FieldIconCss = ({ theme }: { theme: Theme }) => css`
  width: 16px;
  height: 16px;
  color: ${theme.color.content8};
  flex-shrink: 0;
`;

export const FieldIconHashtag = styled(IcHashtagFill)`
  ${FieldIconCss}
`;

export const FieldIconNickname = styled(IcPersonFill)`
  ${FieldIconCss}
  align-self: center;
`;

export const FieldIconLanguages = styled(IcLanguageFill)`
  ${FieldIconCss}
  align-self: center;
`;

export const IcChevronRight = styled(IcChevronRightRegular)`
  width: 16px;
  height: 16px;
  fill: ${({ theme }) => theme.color.contentDeep};
  ${({ theme }) => theme.isRtl && 'transform: scaleX(-1);'}
`;

export const WarningIcon = styled(IcWarningFill)`
  width: 22px;
  height: 22px;
  color: ${({ theme }) => theme.color.accentC1};

  circle {
    fill: ${({ theme }) => theme.color.contentBright};
  }
`;

export const LabelWrap = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 18px 16px;
  ${({ theme }) => InlineFieldBase(theme)}
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

export const TextField = styled.p`
  flex: 1;
  font-size: 14px;
  line-height: 21px;
  font-weight: bold;
  text-align: ${({ theme }) => (theme.isRtl ? 'right' : 'left')};
  color: ${({ theme }) => theme.color.contentDeep};
`;

export const ProfileImage = styled.div`
  position: relative;
  ${({ theme }) => ProfileImageBase(theme)}
  background-size: cover;
`;

export const ProfileImageMainBadge = styled.div`
  padding: 4px 8px;
  position: absolute;
  ${({ theme }) => (theme.isRtl ? 'right' : 'left')}: 8px;
  top: 8px;
  background-color: ${({ theme }) => theme.color.surfaceDeepFixed};
  border-radius: 16px;
  font-weight: 700;
  font-size: 8px;
  line-height: 10px;
  color: ${({ theme }) => theme.color.contentDeep};
`;

export const ProfileImageButtonBase = (theme: Theme) => css`
  position: absolute;
  width: 32px;
  height: 32px;
  ${theme.isRtl ? 'left' : 'right'}: 8px;
  bottom: 8px;
  border-radius: 16px;
  cursor: pointer;
  background-color: ${theme.color.surfaceDeep};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ProfileImageButton = styled.button`
  ${({ theme }) => ProfileImageButtonBase(theme)}
`;

export const ProfileImageLabel = styled.label`
  ${({ theme }) => ProfileImageButtonBase(theme)}
`;

export const NicknamePendingDescription = styled.p`
  margin-top: 8px;
  margin-bottom: 28px;
  color: ${({ theme }) => theme.color.content9};
  font-size: 14px;
  line-height: 18px;
`;
